import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp, formatPrice, getTierPrice, getTierOriginalPrice } from '@/context/AppContext';
import {
  X, CreditCard, Lock, Shield, Check, Wallet, Globe,
  Loader2, Smartphone, QrCode, AlertCircle, ArrowRight,
  Clock, Sparkles,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── Production Feature Flags ── */
const isPayMongoEnabled = true;
const isPayPalEnabled = false;
const isStripeEnabled = false;

/* ── Skeleton Loader ── */
const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-slate-100 rounded-xl ${className}`} />
);

/* ── Accepted Payment Method Badges ── */
const AcceptedBadges = () => (
  <div className="flex items-center justify-center gap-2 mt-3">
    {[
      { label: 'Visa', icon: CreditCard },
      { label: 'GCash', icon: Smartphone },
      { label: 'Maya', icon: Wallet },
      { label: 'QR Ph', icon: QrCode },
    ].map(({ label, icon: Icon }) => (
      <span key={label} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-slate-200 text-[10px] font-medium text-slate-500 shadow-sm">
        <Icon className="w-3 h-3" /> {label}
      </span>
    ))}
  </div>
);

/* ── PayPal Smart Buttons ── */
const PayPalButtonsWrapper = ({ gigData, onSuccess, onError }) => {
  const [paypalReady, setPaypalReady] = useState(false);
  const [paypalClientId, setPaypalClientId] = useState('');

  // Use a ref for the internal order ID to avoid stale closures
  const internalOrderIdRef = React.useRef('');

  // Stable refs for callbacks to prevent PayPal button re-renders
  const gigDataRef = React.useRef(gigData);
  const onSuccessRef = React.useRef(onSuccess);
  const onErrorRef = React.useRef(onError);
  gigDataRef.current = gigData;
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const resp = await axios.get(`${API}/config/payment`);
        const clientId = resp.data.paypal_client_id;
        if (clientId) {
          setPaypalClientId(clientId);
          if (!document.querySelector('script[src*="paypal.com/sdk"]')) {
            const script = document.createElement('script');
            script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD`;
            script.async = true;
            script.onload = () => setPaypalReady(true);
            script.onerror = () => onErrorRef.current('Failed to load PayPal SDK.');
            document.head.appendChild(script);
          } else {
            setPaypalReady(true);
          }
        }
      } catch (err) {
        // PayPal config error handled silently
      }
    };
    fetchConfig();
    // Runs once on mount; API is module-level, onErrorRef is a stable ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render PayPal buttons ONCE when SDK is ready (stable callbacks via refs)
  const containerRef = React.useRef(null);
  useEffect(() => {
    if (!paypalReady || !window.paypal || !containerRef.current) return;
    // Clear container using React ref (avoids innerHTML XSS vector)
    while (containerRef.current.firstChild) {
      containerRef.current.removeChild(containerRef.current.firstChild);
    }

    window.paypal.Buttons({
      // createOrder must return a string (PayPal Order ID)
      createOrder: async () => {
        try {
          const resp = await axios.post(`${API}/checkout/paypal/create-order`, gigDataRef.current);
          const paypalOrderId = resp.data.paypal_order_id;
          internalOrderIdRef.current = resp.data.order_id;
          

          if (!paypalOrderId || typeof paypalOrderId !== 'string') {
            throw new Error('Invalid PayPal Order ID returned from server.');
          }
          return paypalOrderId; // MUST return a string
        } catch (err) {
          const msg = err.response?.data?.detail || err.message || 'Failed to create order.';
          // createOrder error surfaced via onError
          onErrorRef.current(msg);
          throw err; // PayPal SDK requires the promise to reject to close popup cleanly
        }
      },

      onApprove: async (data) => {
        try {
          
          const resp = await axios.post(`${API}/checkout/paypal/capture/${data.orderID}`, {
            order_id: internalOrderIdRef.current,
          });
          if (resp.data.status === 'COMPLETED') {
            onSuccessRef.current(internalOrderIdRef.current);
          } else {
            onErrorRef.current('Payment was not completed. Status: ' + resp.data.status);
          }
        } catch (err) {
          const msg = err.response?.data?.detail || 'Payment capture failed. Please contact support.';
          // capture error surfaced via onError
          onErrorRef.current(msg);
        }
      },

      onError: (err) => {
        // SDK error surfaced via onError callback
        onErrorRef.current('PayPal encountered an error. Please try again or use the card option.');
      },

      onCancel: () => {
        
      },

      style: { color: 'gold', shape: 'rect', label: 'paypal', height: 48, layout: 'vertical', tagline: false },
    }).render(containerRef.current).catch((renderErr) => {
      // render error handled silently
    });
  }, [paypalReady]); // Only depends on paypalReady — callbacks use refs

  if (!paypalClientId) {
    return (
      <div className="text-center py-8">
        <Globe className="w-8 h-8 text-slate-300 mx-auto mb-2" />
        <p className="text-xs text-slate-400">PayPal is being configured. Please try the card option.</p>
      </div>
    );
  }
  if (!paypalReady) {
    return <div className="space-y-3 py-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>;
  }
  return <div ref={containerRef} data-testid="paypal-buttons" className="min-h-[100px]" />;
};

/* ═══════════════════════════════════════════════════
   CHECKOUT MODAL — Enterprise-Grade UI
   ═══════════════════════════════════════════════════ */
export const CheckoutModal = ({ isOpen, onClose, gig, tierIndex }) => {
  const navigate = useNavigate();
  const { setActiveOrder, setUser, currency } = useApp();
  const [activeGateway, setActiveGateway] = useState('paymongo');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [successOrderId, setSuccessOrderId] = useState('');

  if (!gig) return null;
  const tier = gig.tiers[tierIndex];
  const price = getTierPrice(tier, currency);
  const originalPrice = getTierOriginalPrice(tier, currency);
  const deposit = Math.round(price * 0.5);
  const remaining = price - deposit;
  const isRecurring = gig.isRecurring;

  // PayMongo always uses PHP
  const phpPrice = tier.pricePHP;
  const phpDeposit = Math.round(phpPrice * 0.5);

  const gigData = {
    gig_id: gig.id,
    gig_title: gig.title,
    tier_name: tier.name,
    tier_index: tierIndex,
    total: price,
    deposit: deposit,
    delivery: tier.delivery,
  };

  const paymongoData = { ...gigData, total: phpPrice, deposit: phpDeposit };

  /* ── Handlers ── */
  const handlePayMongo = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await axios.post(`${API}/checkout/paymongo`, paymongoData);
      window.location.href = resp.data.checkout_url;
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      setError(detail.length > 200 ? 'Payment gateway error. Please try again or use PayPal.' : detail || 'Unable to connect to payment processor. Please try again.');
      setLoading(false);
    }
  };

  const handlePayPalSuccess = (orderId) => {
    setSuccessOrderId(orderId);
    setSuccess(true);
  };

  const handleGoToDashboard = async () => {
    try {
      const resp = await axios.get(`${API}/orders/${successOrderId}`);
      const order = resp.data;
      setUser({ name: 'Client', email: 'client@medialabs.com' });
      setActiveOrder({
        id: order.order_id, gig, tier, tierIndex,
        total: order.total, deposit: order.deposit, status: order.status,
        phases: order.phases, deliverables: order.deliverables,
      });
    } catch (err) {
      // fallback to in-memory order
    }
    onClose();
    setTimeout(() => navigate('/dashboard'), 200);
  };

  const handleClose = () => {
    if (loading) return; // prevent close during payment
    onClose();
    setTimeout(() => {
      setActiveGateway('paymongo');
      setLoading(false);
      setError('');
      setSuccess(false);
      setSuccessOrderId('');
    }, 300);
  };

  /* ── Gateway config ── */
  const gateways = [
    ...(isPayMongoEnabled ? [{ id: 'paymongo', label: 'Credit Card & E-Wallets', shortLabel: 'Cards & E-Wallets', icon: CreditCard }] : []),
    ...(isPayPalEnabled ? [{ id: 'paypal', label: 'PayPal (International)', shortLabel: 'PayPal', icon: Globe }] : []),
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="checkout-modal-overlay"
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal */}
          <motion.div
            className="relative bg-white w-full sm:max-w-[440px] sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col"
            style={{ maxHeight: 'min(92vh, 720px)' }}
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            data-testid="checkout-modal"
          >
            {/* ═══ FIXED HEADER ═══ */}
            <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-orange/15 to-brand-orange/5 flex items-center justify-center">
                  {success ? (
                    <Check className="w-5 h-5 text-green-500" strokeWidth={2.5} />
                  ) : (
                    <Lock className="w-5 h-5 text-brand-orange" />
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">
                    {success ? 'Sprint Launched!' : 'Secure Checkout'}
                  </h3>
                  <p className="text-[11px] text-slate-400 flex items-center gap-1">
                    <Shield className="w-3 h-3" /> 256-bit SSL encrypted
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={loading}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100 transition-colors duration-150 disabled:opacity-30"
                data-testid="checkout-close"
                aria-label="Close checkout"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            {/* ═══ SCROLLABLE BODY ═══ */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
              <div className="px-5 sm:px-6 py-5">

                {success ? (
                  /* ──────── SUCCESS STATE ──────── */
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-4"
                    data-testid="checkout-success"
                  >
                    <div className="w-18 h-18 rounded-full bg-green-50 border-4 border-green-100 flex items-center justify-center mx-auto mb-5" style={{ width: 72, height: 72 }}>
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                      >
                        <Check className="w-8 h-8 text-green-500" strokeWidth={2.5} />
                      </motion.div>
                    </div>
                    <h4 className="text-xl font-bold text-slate-900 mb-1">Deposit Confirmed!</h4>
                    <p className="text-sm text-slate-500 mb-6">
                      Your sprint for <strong className="text-slate-700">{gig.title}</strong> has officially begun.
                    </p>

                    <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Deposit Paid</span>
                        <span className="font-semibold text-green-600">{formatPrice(deposit, currency)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Tier</span>
                        <span className="font-medium text-slate-700">{tier.name}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Timeline</span>
                        <span className="font-medium text-slate-700">{tier.delivery}</span>
                      </div>
                    </div>

                    <motion.button
                      onClick={handleGoToDashboard}
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full py-3.5 rounded-xl bg-brand-blue text-white text-sm font-bold shadow-lg shadow-brand-blue/20 hover:shadow-xl hover:shadow-brand-blue/25 hover:bg-brand-blue-dark transition-all duration-200 inline-flex items-center justify-center gap-2"
                      data-testid="go-to-dashboard-btn"
                    >
                      Go to Your Dashboard <ArrowRight className="w-4 h-4" />
                    </motion.button>
                  </motion.div>
                ) : (
                  <>
                    {/* ──────── ZONE 1: ORDER SUMMARY ──────── */}
                    <div className="bg-slate-50/80 rounded-xl p-4 mb-4" data-testid="checkout-summary">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Order Summary</p>
                          <h4 className="text-sm font-bold text-slate-900 leading-snug">{gig.title}</h4>
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                              <Sparkles className="w-3 h-3" /> {tier.name}
                            </span>
                            <span className="text-slate-200">|</span>
                            <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                              <Clock className="w-3 h-3" /> {tier.delivery}
                            </span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="text-lg font-extrabold text-slate-900">{formatPrice(price, currency)}</span>
                          {isRecurring && <span className="text-[10px] text-slate-400">/mo</span>}
                        </div>
                      </div>
                    </div>

                    {/* ──────── ZONE 2: FINANCIAL BREAKDOWN ──────── */}
                    <div className="rounded-xl border border-brand-orange/20 bg-gradient-to-br from-brand-orange/[0.04] to-transparent p-4 mb-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Shield className="w-4 h-4 text-brand-orange" />
                        <span className="text-[10px] font-bold text-brand-orange uppercase tracking-widest">
                          {isRecurring ? 'First Month Payment' : '50% Downpayment to Begin'}
                        </span>
                      </div>

                      <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-3xl font-extrabold text-slate-900 tracking-tight">{formatPrice(deposit, currency)}</span>
                        {!isRecurring && (
                          <span className="text-xs text-slate-400 font-medium">of {formatPrice(price, currency)} total</span>
                        )}
                      </div>

                      {!isRecurring && (
                        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-brand-orange/10">
                          <div className="flex-1">
                            <p className="text-[10px] text-slate-400 uppercase tracking-wider">Due Now</p>
                            <p className="text-sm font-bold text-brand-orange">{formatPrice(deposit, currency)}</p>
                          </div>
                          <div className="w-px h-8 bg-slate-200" />
                          <div className="flex-1">
                            <p className="text-[10px] text-slate-400 uppercase tracking-wider">On Delivery</p>
                            <p className="text-sm font-bold text-slate-600">{formatPrice(remaining, currency)}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* ──────── ZONE 3: PAYMENT ACTION ──────── */}

                    {/* Gateway Selector Pill Tabs */}
                    {gateways.length > 1 && (
                      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl mb-4" data-testid="payment-gateway-tabs">
                        {gateways.map((gw) => {
                          const Icon = gw.icon;
                          const isActive = activeGateway === gw.id;
                          return (
                            <button
                              key={gw.id}
                              onClick={() => { setActiveGateway(gw.id); setError(''); }}
                              disabled={loading}
                              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-xs font-semibold transition-all duration-200 ${
                                isActive
                                  ? 'bg-white text-slate-900 shadow-sm'
                                  : 'text-slate-400 hover:text-slate-600'
                              } disabled:opacity-40`}
                              data-testid={`payment-tab-${gw.id}`}
                            >
                              <Icon className="w-4 h-4" />
                              <span className="hidden sm:inline">{gw.label}</span>
                              <span className="sm:hidden">{gw.shortLabel}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Inline Error */}
                    <AnimatePresence>
                      {error && (
                        <motion.div
                          initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                          animate={{ opacity: 1, height: 'auto', marginBottom: 16 }}
                          exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="flex items-start gap-2.5 p-3 rounded-xl bg-red-50/80 border border-red-100">
                            <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-xs font-semibold text-red-600 mb-0.5">Payment error</p>
                              <p className="text-[11px] text-red-500 leading-relaxed">{error}</p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Gateway Content */}
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeGateway}
                        initial={{ opacity: 0, x: 8 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -8 }}
                        transition={{ duration: 0.2 }}
                      >
                        {/* ── PayMongo ── */}
                        {activeGateway === 'paymongo' && (
                          <div data-testid="paymongo-tab-content">
                            <motion.button
                              onClick={handlePayMongo}
                              disabled={loading}
                              whileHover={!loading ? { y: -1 } : {}}
                              whileTap={!loading ? { scale: 0.98 } : {}}
                              className={`w-full py-4 rounded-xl text-white text-sm font-bold transition-all duration-200 inline-flex items-center justify-center gap-2.5
                                ${loading
                                  ? 'bg-slate-400 cursor-not-allowed'
                                  : 'bg-gradient-to-r from-brand-orange to-brand-orange-dark shadow-lg shadow-brand-orange/25 hover:shadow-xl hover:shadow-brand-orange/30'
                                }`}
                              data-testid="paymongo-pay-btn"
                            >
                              {loading ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  <span>Processing Securely...</span>
                                </>
                              ) : (
                                <>
                                  <Lock className="w-4 h-4" />
                                  <span>Pay Deposit &mdash; {formatPrice(deposit, currency)}</span>
                                </>
                              )}
                            </motion.button>
                            <AcceptedBadges />
                          </div>
                        )}

                        {/* ── PayPal ── */}
                        {activeGateway === 'paypal' && isPayPalEnabled && (
                          <div data-testid="paypal-tab-content">
                            <p className="text-xs text-slate-400 text-center mb-4">
                              Pay securely with your PayPal account or international card.
                            </p>
                            <PayPalButtonsWrapper
                              gigData={gigData}
                              onSuccess={handlePayPalSuccess}
                              onError={(msg) => setError(msg)}
                            />
                          </div>
                        )}

                        {/* ── Stripe (hidden) ── */}
                        {activeGateway === 'stripe' && isStripeEnabled && (
                          <div data-testid="stripe-tab-content">
                            <Skeleton className="h-12 w-full mb-3" />
                            <Skeleton className="h-12 w-full" />
                          </div>
                        )}
                      </motion.div>
                    </AnimatePresence>
                  </>
                )}
              </div>
            </div>

            {/* ═══ FIXED FOOTER (trust bar) ═══ */}
            {!success && (
              <div className="shrink-0 px-5 sm:px-6 py-3 border-t border-slate-100 bg-slate-50/50">
                <div className="flex items-center justify-center gap-5 text-[10px] text-slate-400">
                  <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> SSL Secure</span>
                  <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> PCI Compliant</span>
                  <span className="flex items-center gap-1"><CreditCard className="w-3 h-3" /> Encrypted</span>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
